import type { TextareaHTMLAttributes } from 'react';
import { Champ } from './Champ';

/**
 * **Une saisie de plusieurs lignes.**
 *
 * Elle double `Saisie` plutôt que d'en devenir une variante parce que **l'élément natif
 * n'est pas le même** : `<textarea>` n'a ni `type`, ni `value` en attribut, et il porte
 * `rows`. Une prop `multiligne` sur `Saisie` obligerait à brancher sur elle à chaque
 * attribut, ce qui est la duplication déguisée que le niveau 2 de `design-system-first`
 * cherche à éviter — pas celle qu'il produit.
 *
 * **Le libellé peut être invisible sans disparaître.** Sur la zone d'envoi d'un message,
 * un libellé au-dessus du champ prendrait une ligne pour dire ce que le bouton d'à côté
 * dit déjà ; le retirer laisserait un champ que personne au lecteur d'écran ne peut
 * nommer. `libelleCache` est ce compromis, et il est **le seul** : un champ sans libellé
 * du tout n'existe pas dans cette bibliothèque.
 */
type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
  readonly libelle: string;
  readonly libelleCache?: boolean;
  readonly aide?: string;
  readonly erreur?: string;
};

export function ZoneDeTexte({
  libelle,
  libelleCache = false,
  aide,
  erreur,
  ...reste
}: Props) {
  const APPARENCE = [
    'w-full rounded-2 border bg-fond-surface p-2',
    'text-14 text-texte placeholder:text-texte-inactif',
    'focus-visible:outline focus-visible:outline-2',
    'focus-visible:outline-offset-2 focus-visible:outline-focus',
    erreur === undefined ? 'border-bord-champ' : 'border-erreur-bord',
  ].join(' ');

  if (libelleCache) {
    // **Le libellé enveloppe le champ, donc il n'a besoin d'aucun identifiant** : c'est
    // l'imbrication qui les lie, et une paire `id`/`htmlFor` en plus ne ferait que donner
    // deux façons de rater le même lien. Il reste dans l'arbre d'accessibilité, hors de
    // l'écran : `hidden` le retirerait des deux à la fois.
    return (
      <label className="flex w-full flex-col">
        <span className="sr-only">{libelle}</span>
        <textarea
          {...reste}
          aria-invalid={erreur === undefined ? undefined : true}
          className={APPARENCE}
        />
      </label>
    );
  }

  return (
    <Champ libelle={libelle} aide={aide} erreur={erreur}>
      {(liens) => <textarea {...liens} {...reste} className={APPARENCE} />}
    </Champ>
  );
}
